import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout, BlogCard, useBlogSearch } from '../../components/blog';
import { getPostsByTag } from '../../data/blog.generated';
import { useDocumentTitle } from '../../hooks/core';

export const BlogTagPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const decodedTag = tag ? decodeURIComponent(tag) : '';

  const tagPosts = decodedTag ? getPostsByTag(decodedTag) : [];
  const { query, setQuery, results } = useBlogSearch(tagPosts);

  useDocumentTitle(decodedTag ? `#${decodedTag} - Blog` : 'Blog');

  const displayPosts = query ? results : tagPosts;
  const pageDescription = `Browse all posts tagged with "${decodedTag}".`;

  return (
    <BlogLayout
      title={`#${decodedTag}`}
      description={pageDescription}
      breadcrumbs={[{ label: `#${decodedTag}` }]}
      showSearch
      onSearch={setQuery}
      searchValue={query}
    >
      <Helmet>
        <title>#{decodedTag} | SquareUp Blog</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={`#${decodedTag} - SquareUp Blog`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://squareitup.app/blog/tag/${encodeURIComponent(decodedTag)}`} />
        <meta property="og:site_name" content="SquareUp" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`#${decodedTag} - SquareUp Blog`} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={`https://squareitup.app/blog/tag/${encodeURIComponent(decodedTag)}`} />
      </Helmet>
      {displayPosts.length > 0 ? (
        <Grid container spacing={3}>
          {displayPosts.map((post) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.slug}>
              <BlogCard post={post} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {query ? 'No posts found' : 'No posts with this tag yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {query
              ? 'Try a different search term.'
              : 'Check back soon for new content!'}
          </Typography>
        </Paper>
      )}
    </BlogLayout>
  );
};

export default BlogTagPage;
